import { SAMPLE_V2_INTAKE_SUMMARY_DATA } from '@common/fixtures/intakeSummarySamples';
import { Box, Button, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import LexoSwitch from '@/components/LexoSwitch';
import IntakeResponseInformationView from '@/pages/Prospects/ProspectDetails/IntakeResponseInformation/IntakeResponseInformationView';
import {
    downloadIntakeSummaryPdf,
    downloadSampleV1IntakeSummaryPdf,
} from '@/services/ProspectService';

interface PdfLayout {
    label: string;
    isV1: boolean;
    showContactsAsColumnsInIntakeSummary: boolean;
}

const PDF_LAYOUTS: Record<string, PdfLayout> = {
    'v2-rows': { label: 'V2 rows', isV1: false, showContactsAsColumnsInIntakeSummary: false },
    'v2-columns': { label: 'V2 columns', isV1: false, showContactsAsColumnsInIntakeSummary: true },
    'v1-rows': { label: 'V1 rows', isV1: true, showContactsAsColumnsInIntakeSummary: false },
    'v1-columns': { label: 'V1 columns', isV1: true, showContactsAsColumnsInIntakeSummary: true },
};

const PANE_SX = {
    flex: 1,
    minWidth: 0,
    height: '80vh',
    border: '1px solid',
    borderColor: 'neutral.borderColor',
    borderRadius: '8px',
    bgcolor: 'neutral.N_50',
};

export default function IntakeSummariesTab() {
    const [showEmptyAnswers, setShowEmptyAnswers] = useState(true);
    const [pdfLayoutKey, setPdfLayoutKey] = useState('v2-rows');
    const pdfLayout = PDF_LAYOUTS[pdfLayoutKey];

    const pdfQuery = useQuery({
        queryKey: ['sample-intake-summary-pdf', pdfLayoutKey, showEmptyAnswers],
        queryFn: async () => {
            const options = {
                showEmptyAnswersInIntakeSummary: showEmptyAnswers,
                showContactsAsColumnsInIntakeSummary:
                    pdfLayout.showContactsAsColumnsInIntakeSummary,
            };
            const response = pdfLayout.isV1
                ? await downloadSampleV1IntakeSummaryPdf(options)
                : await downloadIntakeSummaryPdf({ ...SAMPLE_V2_INTAKE_SUMMARY_DATA, ...options });
            // Never revoked: there are at most eight small sample PDFs, and the frame and new tab
            // keep needing the URL for as long as the page is open
            return URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        },
        staleTime: Infinity,
        gcTime: Infinity,
    });
    const pdfUrl = pdfQuery.data ?? null;

    return (
        <Stack spacing={3}>
            <Stack spacing={1} sx={{ maxWidth: '720px' }}>
                <Typography sx={{ fontWeight: 600, color: 'text.primary' }}>
                    How to read the samples
                </Typography>
                <Typography variant="body2" sx={{ color: 'neutral.N_500' }}>
                    Both samples are dummy intakes built to cover every case the intake summary
                    handles. Each question&apos;s title says what case it covers and what should
                    happen, and its answer says it again. Numbers are the order the questions have
                    in the form.
                </Typography>
                <Typography variant="body2" sx={{ color: 'neutral.N_500' }}>
                    Look for numbers out of order, anything showing that its title says should not
                    show, and differences between the screen and the PDF. The V2 sample is a Divorce
                    intake with three clients and contact blocks; the V1 sample is a Real Estate
                    Purchase with two purchasers.
                </Typography>
                <Typography variant="body2" sx={{ color: 'neutral.N_500' }}>
                    Known gap: questions a client never touches are not submitted, so they cannot
                    appear in either sample.
                </Typography>
            </Stack>

            <Stack direction="row" flexWrap="wrap" alignItems="center" gap={2}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <LexoSwitch
                        checked={showEmptyAnswers}
                        onChange={(event) => setShowEmptyAnswers(event.target.checked)}
                    />
                    <Typography variant="body2">Show empty answers</Typography>
                </Stack>
                <ToggleButtonGroup
                    value={pdfLayoutKey}
                    exclusive
                    size="small"
                    onChange={(_, value: string | null) => value && setPdfLayoutKey(value)}
                >
                    {Object.entries(PDF_LAYOUTS).map(([key, { label }]) => (
                        <ToggleButton key={key} value={key} sx={{ textTransform: 'none' }}>
                            {label} PDF
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
                <Button
                    variant="outlined"
                    sx={{ textTransform: 'none' }}
                    disabled={!pdfUrl}
                    onClick={() => pdfUrl && window.open(pdfUrl, '_blank')}
                >
                    Open PDF in a new tab
                </Button>
            </Stack>

            <Stack direction={{ xs: 'column', lg: 'row' }} gap={2}>
                <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 600, color: 'text.primary' }}>
                        V2 screen
                    </Typography>
                    <Box sx={{ ...PANE_SX, overflowY: 'auto' }}>
                        <IntakeResponseInformationView
                            {...SAMPLE_V2_INTAKE_SUMMARY_DATA}
                            showEmptyAnswersInIntakeSummary={showEmptyAnswers}
                        />
                    </Box>
                    <Typography variant="body2" sx={{ color: 'neutral.N_500' }}>
                        There is no V1 screen preview: the V1 screen only loads a real
                        prospect&apos;s intake.
                    </Typography>
                </Stack>
                <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 600, color: 'text.primary' }}>
                        {pdfLayout.label} PDF
                    </Typography>
                    {pdfQuery.isError ? (
                        <Typography variant="body2" sx={{ color: 'error.main' }}>
                            Failed to generate the sample PDF
                        </Typography>
                    ) : (
                        <Box
                            component="iframe"
                            title="Sample intake summary PDF"
                            src={pdfUrl ?? undefined}
                            sx={PANE_SX}
                        />
                    )}
                </Stack>
            </Stack>
        </Stack>
    );
}
